import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useFileManagerContext, SET_FILE_MANAGER_STATE } from '../../../providers/FileManager';
import FileSystemService from '../../../service/FileSystemService';

interface NewFolderModalProps {
  onClose: () => void;
  currentPath?: string;
}

const NewFolderModal: React.FC<NewFolderModalProps> = ({ onClose, currentPath = '/' }) => {
  const { fileManagerDispatch } = useFileManagerContext();
  const [folderName, setFolderName] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
    setTouched(true);
  };

  // Validate folder name
  const isValidFolderName = (): boolean => {
    return folderName.trim() !== '' && !/[\\/:*?"<>|]/.test(folderName);
  };

  // Get validation feedback
  const getValidationFeedback = (): string | null => {
    if (!touched) return null;
    if (folderName.trim() === '') return 'Folder name cannot be empty';
    if (/[\\/:*?"<>|]/.test(folderName)) return 'Folder name contains invalid characters';
    return null;
  };

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!isValidFolderName()) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      // Create the folder
      await FileSystemService.createDirectory(currentPath, folderName);
      
      // Close the modal
      onClose();
      
      // Refresh the file manager
      fileManagerDispatch({
        type: SET_FILE_MANAGER_STATE,
        payload: { refreshTimestamp: Date.now() }
      });
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(`Failed to create folder: ${err instanceof Error ? err.message : String(err)}`);
      setIsCreating(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Folder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Folder Name</Form.Label>
            <Form.Control
              type="text"
              value={folderName}
              onChange={handleChange}
              placeholder="Enter folder name"
              isInvalid={touched && !isValidFolderName()}
              disabled={isCreating}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {getValidationFeedback()}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Folder will be created at: {currentPath === '/' ? '/' : `${currentPath}/`}{folderName || '[folder name]'}
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCreateFolder} 
          disabled={!isValidFolderName() || isCreating}
        >
          {isCreating ? 'Creating...' : 'Create Folder'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewFolderModal;